import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../common/Card';
import Input from '../common/Input';
import Button from '../common/Button';
import TermsModal from './TermsModal';

const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [formValid, setFormValid] = useState(false);

  const { register, login } = useAuth();
  const navigate = useNavigate();

  // 邮箱正则：基本邮箱格式验证
  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  // 密码正则：至少8位
  const passwordRegex = useMemo(() => /^.{8,}$/, []);

  // 验证表单
  useEffect(() => {
    // 重置错误信息
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');

    // 验证邮箱
    if (email && !emailRegex.test(email)) {
      setEmailError('请输入有效的邮箱格式');
    }

    // 验证密码
    if (password && !passwordRegex.test(password)) {
      setPasswordError('密码长度必须至少为8位');
    }

    // 验证确认密码
    if (confirmPassword && password !== confirmPassword) {
      setConfirmPasswordError('两次输入的密码不一致');
    }

    // 检查表单是否有效
    const isValid =
      email &&
      password &&
      confirmPassword &&
      agreedToTerms &&
      emailRegex.test(email) &&
      passwordRegex.test(password) &&
      password === confirmPassword;

    setFormValid(isValid);
  }, [email, password, confirmPassword, agreedToTerms, emailRegex, passwordRegex]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 再次验证表单
    if (!emailRegex.test(email)) {
      setEmailError('请输入有效的邮箱格式');
      return;
    }

    if (!passwordRegex.test(password)) {
      setPasswordError('密码长度必须至少为8位');
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('两次输入的密码不一致');
      return;
    }

    if (!agreedToTerms) {
      setError('请先同意用户协议与隐私政策');
      return;
    }

    setLoading(true);

    try {
      await register(email, password);
      // 注册成功后自动登录
      await login(email, password);
      navigate('/evaluations');
    } catch (err) {
      setError(err.message || '注册失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>注册</CardTitle>
        <CardDescription>
          创建一个新账号
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              邮箱
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="请输入邮箱地址"
              error={emailError}
            />
            {!emailError && email && (
              <p className="text-xs text-muted-foreground mt-1">请输入有效的邮箱格式</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">
              密码
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="请输入至少8位的密码"
              error={passwordError}
            />
            {!passwordError && (
              <p className="text-xs text-muted-foreground mt-1">密码长度必须至少为8位</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="text-sm font-medium">
              确认密码
            </label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="请再次输入密码"
              error={confirmPasswordError}
            />
          </div>

          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              id="terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="terms" className="text-sm text-gray-600">
              我已阅读并同意{' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-blue-600 hover:underline"
              >
                《用户协议与隐私政策》
              </button>
            </label>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !formValid}
          >
            {loading ? '注册中...' : '注册'}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex justify-center">
        <p className="text-sm text-muted-foreground">
          已有账号?{' '}
          <Link to="/login" className="text-primary hover:underline">
            立即登录
          </Link>
        </p>
      </CardFooter>
    </Card>
    
    <TermsModal 
      isOpen={showTermsModal}
      onClose={() => setShowTermsModal(false)}
      onAccept={() => {
        setAgreedToTerms(true);
        setShowTermsModal(false);
      }}
    />
    </>
  );
};

export default RegisterForm;
